import { useState, useRef } from "react";
import { uploadOfficerDocument, deleteOfficerDocument, validateFile } from "../utils/fileUpload";
import styles from "./ProfileEditor.module.css";

export default function ProfileEditor({ currentUser, onSave, onUpdateEmail, onChangePassword, onUpdateDocuments }) {
  const currentPrefs = currentUser.notificationPreferences || { email: true, sms: false };
  const [form, setForm] = useState({
    name: currentUser.name || "",
    badge: currentUser.badge || "",
    phone: currentUser.phone || "",
    email: currentUser.email || "",
  });
  const [notifPrefs, setNotifPrefs] = useState({
    email: currentPrefs.email !== false,
    sms: currentPrefs.sms === true,
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  // Email change requires current password for re-auth
  const [emailPassword, setEmailPassword] = useState("");
  const [emailError, setEmailError] = useState("");

  // Password change state
  const [pwForm, setPwForm] = useState({ current: "", new: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState("");

  // Document upload state
  const [docUploading, setDocUploading] = useState(false);
  const [docError, setDocError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const documents = currentUser.documents || [];

  // Additional emails state
  const [additionalEmails, setAdditionalEmails] = useState(currentUser.additionalEmails || []);
  const [newEmail, setNewEmail] = useState("");
  const [addEmailError, setAddEmailError] = useState("");

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { setDocError(err); return; }
    setDocError("");
    setDocUploading(true);
    try {
      const doc = await uploadOfficerDocument(currentUser.id, file);
      await onUpdateDocuments([...documents, doc]);
    } catch (err) {
      setDocError(err.message || "Upload failed.");
    } finally {
      setDocUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDocDelete = async (index) => {
    const doc = documents[index];
    setDocError("");
    try {
      await deleteOfficerDocument(doc);
      const updated = documents.filter((_, i) => i !== index);
      await onUpdateDocuments(updated);
    } catch (err) {
      setDocError(err.message || "Delete failed.");
    }
  };

  const emailChanged = form.email !== (currentUser.email || "");
  const additionalEmailsChanged = JSON.stringify(additionalEmails) !== JSON.stringify(currentUser.additionalEmails || []);
  const dirty = form.name !== (currentUser.name || "") ||
    form.badge !== (currentUser.badge || "") ||
    form.phone !== (currentUser.phone || "") ||
    emailChanged ||
    additionalEmailsChanged ||
    notifPrefs.email !== (currentPrefs.email !== false) ||
    notifPrefs.sms !== (currentPrefs.sms === true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError("");

    // If email changed, require password for re-auth
    if (emailChanged && !emailPassword) {
      setEmailError("Enter your current password to change your email.");
      return;
    }

    setSaving(true);
    setSuccess(false);
    try {
      // Update Firebase Auth email first if changed
      if (emailChanged) {
        await onUpdateEmail(form.email, emailPassword);
        setEmailPassword("");
      }
      await onSave({
        ...form,
        additionalEmails,
        notificationPreferences: notifPrefs,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const msg = err?.code === "auth/wrong-password" || err?.code === "auth/invalid-credential"
        ? "Incorrect password. Please try again."
        : err.message || "Failed to update profile.";
      setEmailError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError("");

    if (pwForm.new.length < 8) {
      setPwError("New password must be at least 8 characters.");
      return;
    }
    if (!/[A-Z]/.test(pwForm.new)) {
      setPwError("New password must contain an uppercase letter.");
      return;
    }
    if (!/[a-z]/.test(pwForm.new)) {
      setPwError("New password must contain a lowercase letter.");
      return;
    }
    if (!/[0-9]/.test(pwForm.new)) {
      setPwError("New password must contain a number.");
      return;
    }
    if (!/[^A-Za-z0-9]/.test(pwForm.new)) {
      setPwError("New password must contain a special character.");
      return;
    }
    if (pwForm.new !== pwForm.confirm) {
      setPwError("New passwords do not match.");
      return;
    }

    setPwSaving(true);
    setPwSuccess(false);
    try {
      await onChangePassword(pwForm.current, pwForm.new);
      setPwForm({ current: "", new: "", confirm: "" });
      setPwSuccess(true);
      setTimeout(() => setPwSuccess(false), 3000);
    } catch (err) {
      const msg = err?.code === "auth/wrong-password" || err?.code === "auth/invalid-credential"
        ? "Current password is incorrect."
        : err.message || "Failed to change password.";
      setPwError(msg);
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div>
      <h2 className={styles.heading}>My Profile</h2>

      <div className={styles.infoCard}>
        <p className={styles.infoRow}><span className={styles.label}>Role:</span> {currentUser.role}</p>
        <p className={styles.infoRow}><span className={styles.label}>Status:</span> {currentUser.status}</p>
      </div>

      <form onSubmit={handleSubmit} className={styles.formCard}>
        <div className={styles.field}>
          <label htmlFor="profile-name" className={styles.fieldLabel}>Full Name</label>
          <input id="profile-name" className={styles.input} type="text" value={form.name} onChange={set("name")} required />
        </div>

        <div className={styles.field}>
          <label htmlFor="profile-badge" className={styles.fieldLabel}>Badge Number</label>
          <input id="profile-badge" className={styles.input} type="text" value={form.badge} onChange={set("badge")} required />
        </div>

        <div className={styles.field}>
          <label htmlFor="profile-phone" className={styles.fieldLabel}>Phone</label>
          <input id="profile-phone" className={styles.input} type="tel" value={form.phone} onChange={set("phone")} />
        </div>

        <div className={styles.field}>
          <label htmlFor="profile-email" className={styles.fieldLabel}>Email</label>
          <input id="profile-email" className={styles.input} type="email" value={form.email} onChange={set("email")} required />
        </div>

        <div className={styles.field}>
          <div className={styles.fieldLabel}>Additional Email Addresses</div>
          <p className={styles.addEmailHint}>
            Add extra emails to receive all notifications. Up to 5 allowed.
          </p>
          {additionalEmails.length > 0 && (
            <div className={styles.emailList}>
              {additionalEmails.map((addr, i) => (
                <div key={i} className={styles.emailRow}>
                  <span className={styles.emailAddr}>{addr}</span>
                  <button
                    type="button"
                    className={styles.emailRemoveBtn}
                    onClick={() => setAdditionalEmails((prev) => prev.filter((_, idx) => idx !== i))}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          {additionalEmails.length < 5 && (
            <div className={styles.addEmailRow}>
              <input
                className={styles.input}
                type="email"
                placeholder="Add email address"
                value={newEmail}
                onChange={(e) => { setNewEmail(e.target.value); setAddEmailError(""); }}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className={styles.addEmailBtn}
                disabled={!newEmail}
                onClick={() => {
                  const trimmed = newEmail.trim().toLowerCase();
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
                    setAddEmailError("Enter a valid email address.");
                    return;
                  }
                  if (trimmed === form.email.toLowerCase() || additionalEmails.some((e) => e.toLowerCase() === trimmed)) {
                    setAddEmailError("This email is already added.");
                    return;
                  }
                  setAdditionalEmails((prev) => [...prev, trimmed]);
                  setNewEmail("");
                  setAddEmailError("");
                }}
              >
                Add
              </button>
            </div>
          )}
          {addEmailError && <div className={styles.errorMsg}>{addEmailError}</div>}
        </div>

        {emailChanged && (
          <div className={styles.field}>
            <label htmlFor="profile-email-pw" className={styles.fieldLabel}>Current Password (required to change email)</label>
            <input
              id="profile-email-pw"
              className={styles.input}
              type="password"
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
              required
            />
          </div>
        )}

        {emailError && <div className={styles.errorMsg}>{emailError}</div>}

        <div className={styles.notifSection}>
          <h3 className={styles.notifHeading}>Notification Preferences</h3>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={notifPrefs.email}
              onChange={(e) => setNotifPrefs((p) => ({ ...p, email: e.target.checked }))}
            />
            <span className={styles.toggleLabel}>Email notifications</span>
          </label>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={notifPrefs.sms}
              onChange={(e) => setNotifPrefs((p) => ({ ...p, sms: e.target.checked }))}
            />
            <span className={styles.toggleLabel}>SMS notifications <span className={styles.comingSoon}>(Coming soon)</span></span>
          </label>
        </div>

        <div className={styles.actions}>
          <button type="submit" className={styles.saveBtn} disabled={!dirty || saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
          {success && <span className={styles.successMsg}>Profile updated!</span>}
        </div>
      </form>

      {/* Password Change Section */}
      <form onSubmit={handleChangePassword} className={styles.formCard} style={{ marginTop: 20 }}>
        <h3 className={styles.notifHeading}>Change Password</h3>

        <div className={styles.field}>
          <label htmlFor="pw-current" className={styles.fieldLabel}>Current Password</label>
          <input
            id="pw-current"
            className={styles.input}
            type="password"
            value={pwForm.current}
            onChange={(e) => setPwForm((p) => ({ ...p, current: e.target.value }))}
            required
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="pw-new" className={styles.fieldLabel}>New Password</label>
          <input
            id="pw-new"
            className={styles.input}
            type="password"
            placeholder="Min 8 chars, upper, lower, number, special"
            value={pwForm.new}
            onChange={(e) => setPwForm((p) => ({ ...p, new: e.target.value }))}
            required
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="pw-confirm" className={styles.fieldLabel}>Confirm New Password</label>
          <input
            id="pw-confirm"
            className={styles.input}
            type="password"
            value={pwForm.confirm}
            onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))}
            required
          />
        </div>

        {pwError && <div className={styles.errorMsg}>{pwError}</div>}

        <div className={styles.actions}>
          <button type="submit" className={styles.saveBtn} disabled={pwSaving || !pwForm.current || !pwForm.new || !pwForm.confirm}>
            {pwSaving ? "Changing..." : "Change Password"}
          </button>
          {pwSuccess && <span className={styles.successMsg}>Password changed!</span>}
        </div>
      </form>

      {/* Documents Section */}
      <div className={styles.formCard} style={{ marginTop: 20 }}>
        <h3 className={styles.notifHeading}>Documents</h3>
        <p className={styles.docHint}>
          Upload documents required by your agency (e.g. 1099, certifications). Max 10 MB per file.
        </p>

        {documents.length === 0 ? (
          <p className={styles.emptyText}>No documents uploaded.</p>
        ) : (
          <div className={styles.docList}>
            {documents.map((doc, i) => (
              <div key={i} className={styles.docRow}>
                <a href={doc.url} target="_blank" rel="noopener noreferrer" className={styles.docLink}>
                  {doc.name}
                </a>
                <span className={styles.docMeta}>
                  {(doc.size / 1024).toFixed(0)} KB
                </span>
                <button
                  type="button"
                  className={styles.docDeleteBtn}
                  onClick={() => handleDocDelete(i)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {docError && <div className={styles.errorMsg}>{docError}</div>}

        <div className={styles.actions}>
          <label className={styles.uploadBtn}>
            {docUploading ? "Uploading..." : "Upload Document"}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.txt"
              onChange={handleDocUpload}
              disabled={docUploading}
              style={{ display: "none" }}
            />
          </label>
        </div>
      </div>
    </div>
  );
}
